import { makeStyles } from "@material-ui/core/styles";
import { NumberRangeFilterField } from "components/filters/NumberRangeFilterField";
import { ToggleStateFilter } from "components/filters/ToggleStateFilter";
import { DateInput, NumberInputEx } from "components/inputs";
import { envSettings } from "core/EnvSettings";
import { OrderStatusActive } from "entities/OrderStatus";
import {
	PalletReservationState,
	PalletReservationStateName,
} from "entities/PalletReservationState";
import { RecordContextProvider } from "ra-core";
import React, { useRef } from "react";
import {
	AutocompleteInput,
	Filter,
	ReferenceInput,
	TextInput,
	minValue,
	useListContext,
} from "react-admin";

import { sortDefaultValue } from "../PickingList";
import { IsSelectedViewToggleFilter } from "./IsSelectedViewToggleFilter";
import { OperationModeToggleFilter, operationModeToggleEnum } from "./OperationModeToggleFilter";

const useStyles = makeStyles({
	filterContainer: {
		"& .filter-field": {
			display: "flex",
			alignItems: "center",
		},
		display: "flex",
		alignItems: "center",
	},
});

export const isReservedToggleStates = [
	{
		value: PalletReservationState.RESERVED,
		name: `osfc.filters.state.${PalletReservationStateName[PalletReservationState.RESERVED]}`,
	},
	{
		value: PalletReservationState.AVAILABLE,
		name: `osfc.filters.state.${PalletReservationStateName[PalletReservationState.AVAILABLE]}`,
	},
	{
		value: PalletReservationState.OTHER_ORDERS,
		name: `osfc.filters.state.${PalletReservationStateName[PalletReservationState.OTHER_ORDERS]}`,
	},
	{
		value: PalletReservationState.ALL,
		name: `osfc.filters.state.${PalletReservationStateName[PalletReservationState.ALL]}`,
	},
];

export const PickingListFilter = ({ record, ...props }) => {
	const classes = useStyles();
	const { setFilters, setPerPage, setSort, filterValues } = useListContext();
	const filterValuesRef = useRef(filterValues);
	filterValuesRef.current = filterValues;

	const {
		translate,
		isReservationMode,
		isBoxMovementMode,
		isLoadingMode,
		isSelectedViewMode,
		setIsSelectedViewMode,
		operationMode,
		setOperationMode,
		filterDefaultValues,
		orderId,
		orderStatus,
	} = props;
	const isOperationScannerMode = operationMode === operationModeToggleEnum[0].value;
	const isDisableScaningAndOperationFilters = !OrderStatusActive.includes(orderStatus);
	const isFilterDisabled =
		isSelectedViewMode ||
		(!envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE && isOperationScannerMode);

	React.useEffect(() => {
		if (
			!envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE &&
			isOperationScannerMode &&
			!isBoxMovementMode
		) {
			const currentReservationState = filterValuesRef.current?.reservation_state;
			setFilters({
				...filterDefaultValues,
				reservation_state: currentReservationState,
			});
			setPerPage(100);
			setSort(sortDefaultValue.field, sortDefaultValue.sort);
		}
	}, [
		filterDefaultValues,
		envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE,
		isOperationScannerMode,
		isBoxMovementMode,
		setFilters,
		setPerPage,
		setSort,
	]);

	return (
		<RecordContextProvider value={{}}>
			<Filter {...props} className={classes.filterContainer}>
				{!envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE && (
					<OperationModeToggleFilter
						{...props}
						operationMode={operationMode}
						setOperationMode={setOperationMode}
						disabled={(state) =>
							isDisableScaningAndOperationFilters ||
							isSelectedViewMode ||
							(!envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE &&
								!isReservationMode &&
								!isLoadingMode &&
								!isBoxMovementMode &&
								state.value === operationModeToggleEnum[0].value)
						}
						alwaysOn
					/>
				)}
				{!isLoadingMode && (
					<ToggleStateFilter
						{...props}
						source="reservation_state"
						toggleStates={isReservedToggleStates}
						disabled={isDisableScaningAndOperationFilters || isFilterDisabled}
						alwaysOn
					/>
				)}
				{isReservationMode || isLoadingMode ? (
					<IsSelectedViewToggleFilter
						{...props}
						isSelectedViewMode={isSelectedViewMode}
						setIsSelectedViewMode={setIsSelectedViewMode}
						alwaysOn
					/>
				) : null}
				<NumberInputEx
					label={translate("tab.pallets.pallet_id")}
					source="id"
					disabled={isFilterDisabled}
					alwaysOn
				/>
				<TextInput
					label={translate("tab.pallets.sscc")}
					source="sscc"
					disabled={isFilterDisabled}
					alwaysOn
				/>
				<TextInput
					label={translate("tab.pallets.sscc_original")}
					source="sscc_original"
					disabled={isFilterDisabled}
				/>
				<TextInput
					label={translate("tab.pallets.gtin")}
					source="gtin"
					disabled={isFilterDisabled}
				/>
				<NumberInputEx
					type="digits"
					label={translate("tab.pallets.external_id")}
					source="ext_id"
					disabled={isFilterDisabled}
					alwaysOn
				/>

				<ReferenceInput
					label={translate("tab.pallets.product_number")}
					source="product_number"
					reference="product-numbers"
					filter={{
						no_range: true,
						order_id: orderId,
					}}
					disabled={isFilterDisabled}
					alwaysOn
					resettable
				>
					<AutocompleteInput optionText="product_number" />
				</ReferenceInput>

				<ReferenceInput
					label={translate("tab.pallets.product_name")}
					source="product_name"
					reference="product-names"
					filter={{
						no_range: true,
						order_id: orderId,
					}}
					disabled={isFilterDisabled}
					alwaysOn
					resettable
				>
					<AutocompleteInput optionText="product_name" />
				</ReferenceInput>

				<TextInput
					label={translate("tab.pallets.ceel_number")}
					source="ceel_number"
					disabled={isFilterDisabled}
					alwaysOn
				/>
				<TextInput
					label={translate("tab.pallets.batch_number")}
					source="batch_number"
					disabled={isFilterDisabled}
				/>
				<TextInput
					label={translate("tab.pallets.lot_number")}
					source="lot_number"
					disabled={isFilterDisabled}
				/>
				<TextInput
					label={translate("tab.pallets.packing_station")}
					source="packing_station"
					disabled={isFilterDisabled}
				/>

				<DateInput
					label={translate("tab.pallets.expiration_datetime")}
					source="expiration_datetime"
					disabled={isFilterDisabled}
				/>
				<DateInput
					label={translate("tab.pallets.production_datetime")}
					source="production_datetime"
					disabled={isFilterDisabled}
				/>
				<TextInput
					label={translate("tab.pallets.incoming_order_ref")}
					source="incoming_order_ref"
				/>

				<NumberRangeFilterField
					source="available_weight"
					label="tab.picking_list.available_weight"
					debounceTime={300}
					validate={minValue(0)}
					disabled={isFilterDisabled}
				/>
				<NumberRangeFilterField
					source="available_quantity"
					label="tab.picking_list.available_quantity"
					debounceTime={300}
					validate={minValue(0)}
					disabled={isFilterDisabled}
				/>
				<NumberRangeFilterField
					source="reserved_weight"
					label="tab.picking_list.reserved_weight"
					debounceTime={300}
					validate={minValue(0)}
					disabled={isFilterDisabled}
				/>
				<NumberRangeFilterField
					source="reserved_quantity"
					label="tab.picking_list.reserved_quantity"
					debounceTime={300}
					validate={minValue(0)}
					disabled={isFilterDisabled}
				/>
			</Filter>
		</RecordContextProvider>
	);
};
